import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { Drawer } from "./drawer";

export interface SidebarNavItem {
  /** Stable id — matched against `activeId`. */
  id: string;
  label: React.ReactNode;
  href?: string;
  /** Icon element (e.g. a lucide icon); rendered aria-hidden. */
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  /** Optional nested children (renders a collapsible sub-tree). */
  children?: SidebarNavItem[];
  /** Optional external onClick handler; host wires its router here. */
  onClick?: (item: SidebarNavItem) => void;
}

export interface SidebarNavGroup {
  label?: string;
  items: SidebarNavItem[];
}

export type LinkComponentType = React.ComponentType<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }
>;

export interface SidebarNavProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  groups: SidebarNavGroup[];
  activeId?: string;
  /** Predicate alternative to activeId. */
  isActive?: (item: SidebarNavItem) => boolean;
  /** Inject the host's router <Link>. Falls back to plain <a href>. */
  linkComponent?: LinkComponentType;
  /** Global navigate callback (called with the item). */
  onNavigate?: (item: SidebarNavItem) => void;
  /** Collapse groups that have labels. Default false. */
  collapsible?: boolean;
  defaultCollapsedGroupLabels?: string[];
  /** Mobile drawer: renders a trigger and a slide-up sheet on small screens. */
  drawer?: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    trigger?: React.ReactNode;
    title?: React.ReactNode;
  };
}

function isActiveDefault(
  item: SidebarNavItem,
  activeId?: string,
  isActive?: (i: SidebarNavItem) => boolean,
): boolean {
  if (isActive) return isActive(item);
  return activeId !== undefined && item.id === activeId;
}

function NavLink({
  item,
  level,
  active,
  linkComponent,
  onNavigate,
}: {
  item: SidebarNavItem;
  level: number;
  active: boolean;
  linkComponent?: LinkComponentType;
  onNavigate?: (item: SidebarNavItem) => void;
}) {
  const className = cn(
    "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring",
    level === 0 ? "text-sidebar-foreground" : "text-sidebar-foreground/80",
    active
      ? "bg-sidebar-accent/10 text-sidebar-accent"
      : "hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground",
  );
  const inner = (
    <>
      {item.icon ? (
        <span aria-hidden="true" className="shrink-0 [&_svg]:size-4">
          {item.icon}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "ml-1 size-1.5 shrink-0 rounded-full",
            active ? "bg-sidebar-accent" : "bg-transparent",
          )}
        />
      )}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground">{item.badge}</span>
      ) : null}
    </>
  );
  if (item.href) {
    const props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string } = {
      href: item.href,
      "aria-current": active ? "page" : undefined,
      className,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        onNavigate?.(item);
        item.onClick?.(item);
        if (linkComponent) e.stopPropagation();
      },
    };
    return linkComponent ? (
      React.createElement(linkComponent, props, inner)
    ) : (
      <a {...props}>{inner}</a>
    );
  }
  return (
    <button type="button" className={cn(className, "w-full text-left")} onClick={() => {
      onNavigate?.(item);
      item.onClick?.(item);
    }}>
      {inner}
    </button>
  );
}

function NavItem({
  item,
  level,
  activeId,
  isActive,
  linkComponent,
  onNavigate,
}: Omit<SidebarNavProps, "groups" | "drawer" | "className"> & {
  item: SidebarNavItem;
  level: number;
}) {
  const active = isActiveDefault(item, activeId, isActive);
  const hasChildren = Boolean(item.children?.length);
  const [open, setOpen] = React.useState(active || level > 0);

  React.useEffect(() => {
    if (active && hasChildren) setOpen(true);
  }, [active, hasChildren]);

  return (
    <li>
      <div className="flex items-center gap-0.5">
        <div className="min-w-0 flex-1">
          <NavLink
            item={item}
            level={level}
            active={active}
            linkComponent={linkComponent}
            onNavigate={onNavigate}
          />
        </div>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={`Toggle ${typeof item.label === "string" ? item.label : "section"}`}
            onClick={() => setOpen((o) => !o)}
            className="rounded-md p-1 text-muted-foreground hover:bg-sidebar-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {open ? (
              <ChevronDown className="size-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-4" aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>
      {hasChildren && open ? (
        <ul className="mt-0.5 space-y-0.5 border-l border-sidebar-border pl-2">
          {item.children!.map((child) => (
            <NavItem
              key={child.id}
              item={child}
              level={level + 1}
              activeId={activeId}
              isActive={isActive}
              linkComponent={linkComponent}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function NavGroups({
  groups,
  collapsible,
  defaultCollapsedGroupLabels = [],
  ...nav
}: Omit<SidebarNavProps, "drawer" | "className">) {
  return (
    <>
      {groups.map((group, gi) => (
        <NavGroup
          key={group.label ?? `group-${gi}`}
          group={group}
          collapsible={collapsible}
          initiallyCollapsed={
            group.label !== undefined && defaultCollapsedGroupLabels.includes(group.label)
          }
          {...nav}
        />
      ))}
    </>
  );
}

function NavGroup({
  group,
  collapsible,
  initiallyCollapsed,
  ...nav
}: {
  group: SidebarNavGroup;
  collapsible?: boolean;
  initiallyCollapsed: boolean;
} & Omit<SidebarNavProps, "groups" | "drawer" | "className">) {
  const [open, setOpen] = React.useState(!initiallyCollapsed);
  const listId = `asrt-sng-${React.useId()}`;
  const heading = group.label ? (
    collapsible ? (
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {group.label}
        <ChevronDown
          className={cn("size-3.5 transition-transform motion-safe:duration-200", !open && "-rotate-90")}
          aria-hidden="true"
        />
      </button>
    ) : (
      <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {group.label}
      </div>
    )
  ) : null;

  return (
    <div className="mb-1">
      {heading}
      {(!collapsible || open) ? (
        <ul id={listId} className="space-y-0.5" role="list">
          {group.items.map((item) => (
            <NavItem key={item.id} item={item} level={0} {...nav} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const SidebarNav = React.forwardRef<HTMLElement, SidebarNavProps>(
  ({ groups, className, drawer, ...nav }, ref) => {
    const navEl = (
      <nav
        ref={drawer ? undefined : ref}
        aria-label="Sidebar navigation"
        className={cn(
          "flex h-full w-full flex-col overflow-y-auto bg-sidebar p-3 text-sidebar-foreground",
          className,
        )}
      >
        <NavGroups groups={groups} {...nav} />
      </nav>
    );

    if (drawer) {
      return (
        <Drawer
          open={drawer.open}
          onOpenChange={drawer.onOpenChange}
          trigger={drawer.trigger}
          title={drawer.title}
          side="left"
        >
          {navEl}
        </Drawer>
      );
    }
    return navEl;
  },
);
SidebarNav.displayName = "SidebarNav";

export { SidebarNav };
